import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  ShoppingBag, Package, ShieldAlert, Star, MessageSquare,
  ArrowRight, Bell, Activity, RefreshCw, Loader2,
  AlertTriangle, CheckCircle2, XCircle, Clock,
  BarChart3, Zap, Play, Mail, ExternalLink, Maximize2, X, Copy, Check,
} from 'lucide-react';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/Admin/Layout/AdminLayout';
import { useSeller } from '../../context/SellerContext';
import apiClient from '../../services/api/client';
import { ENDPOINTS } from '../../services/api/endpoints';

// ─── Content Viewer Modal ─────────────────────────────────────────────────────

function ContentViewerModal({ isOpen, onClose, title, content }) {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = content || '';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-[#FFF4E6] flex items-center justify-center flex-shrink-0">
              <Mail size={14} className="text-[#C45500]" />
            </div>
            <h3 className="text-sm font-semibold text-slate-800 truncate">{title || 'Notification Content'}</h3>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleCopy}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                copied
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : 'bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100'
              }`}
            >
              {copied ? <Check size={12} /> : <Copy size={12} />}
              {copied ? 'Copied!' : 'Copy'}
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-5">
          <pre className="text-[13px] text-slate-700 whitespace-pre-wrap leading-relaxed font-sans">
            {content}
          </pre>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── Notification Type Config ────────────────────────────────────────────────

const NOTIFICATION_TYPES = [
  {
    id: 'DAILY_ORDER_SALES',
    title: 'Daily Order & Sales',
    icon: ShoppingBag,
    iconBg: 'bg-[#FFF4E6]',
    iconColor: 'text-[#C45500]',
    accentBorder: 'border-[#FFD89E]',
    route: '/sales',
    endpoint: ENDPOINTS.REPORTS.DAILY_SUMMARY,
  },
  {
    id: 'PRODUCT_LISTING_STATUS',
    title: 'Product Listing Status',
    icon: Package,
    iconBg: 'bg-emerald-50',
    iconColor: 'text-emerald-600',
    accentBorder: 'border-emerald-200',
    route: '/products',
    endpoint: ENDPOINTS.REPORTS.LISTING_STATUS,
  },
  {
    id: 'PRODUCT_HIJACK',
    title: 'Hijack Detection',
    icon: ShieldAlert,
    iconBg: 'bg-red-50',
    iconColor: 'text-red-600',
    accentBorder: 'border-red-200',
    route: '/hijack-alerts',
    endpoint: ENDPOINTS.REPORTS.HIJACK_CHECK,
  },
  {
    id: 'FEEDBACK_REVIEW',
    title: 'Feedback & Reviews',
    icon: MessageSquare,
    iconBg: 'bg-violet-50',
    iconColor: 'text-violet-600',
    accentBorder: 'border-violet-200',
    route: '/reviews-feedback',
    endpoint: ENDPOINTS.REPORTS.FEEDBACK,
  },
  {
    id: 'REVIEW_REQUEST',
    title: 'Review Requests',
    icon: Star,
    iconBg: 'bg-amber-50',
    iconColor: 'text-amber-600',
    accentBorder: 'border-amber-200',
    route: '/review-requests',
    endpoint: ENDPOINTS.FEEDBACK_REQUESTS.GENERATE_NOW,
  },
  {
    id: 'POLICY_WARNING',
    title: 'Policy Warnings',
    icon: AlertTriangle,
    iconBg: 'bg-orange-50',
    iconColor: 'text-orange-600',
    accentBorder: 'border-orange-200',
    route: '/policy-warnings',
    endpoint: ENDPOINTS.POLICY_WARNINGS.SYNC,
  },
  {
    id: 'SPONSORED_AD_PERFORMANCE',
    title: 'Ad Performance',
    icon: BarChart3,
    iconBg: 'bg-pink-50',
    iconColor: 'text-pink-500',
    accentBorder: 'border-pink-200',
    route: '/ad-performance',
    endpoint: ENDPOINTS.AD_PERFORMANCE.RUN_NOW,
  },
];

const FREQ_LABELS = {
  every_1_hour: 'Every hour',
  every_2_hours: 'Every 2 hours',
  every_4_hours: 'Every 4 hours',
  every_6_hours: 'Every 6 hours',
  daily: 'Daily',
  weekly: 'Weekly',
  biweekly: 'Bi-weekly',
  monthly: 'Monthly',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffM = Math.floor(diffMs / 60000);
  if (diffM < 1) return 'just now';
  if (diffM < 60) return `${diffM}m ago`;
  const diffH = Math.floor(diffM / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 30) return `${diffD}d ago`;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

function formatTime(timeStr) {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':');
  const hour = parseInt(h);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour % 12 || 12;
  return `${h12}:${m} ${ampm}`;
}

function formatDateTime(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-IN', {
    day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit',
  });
}

// ─── Notification Badge Card ─────────────────────────────────────────────────

function NotificationBadge({ type, data, running, onRunNow, onNavigate }) {
  const [showContent, setShowContent] = useState(false);
  const Icon = type.icon;
  const config = data || {};
  const delivery = config.lastDelivery;
  const isEnabled = config.isEnabled === true;
  const hasDelivery = delivery != null && delivery.subject != null;
  const isBackendRunning = config.isRunning === true;

  const status = delivery?.status;
  const statusStyles = {
    SENT: { dot: 'bg-emerald-500', text: 'text-emerald-700' },
    DELIVERED: { dot: 'bg-emerald-500', text: 'text-emerald-700' },
    FAILED: { dot: 'bg-red-500', text: 'text-red-600' },
    QUEUED: { dot: 'bg-amber-500', text: 'text-amber-700' },
    GENERATED: { dot: 'bg-[#232F3E]', text: 'text-[#C45500]' },
  };
  const ss = statusStyles[status] || { dot: 'bg-slate-300', text: 'text-slate-400' };

  return (
    <div className={`bg-white rounded-xl border ${hasDelivery ? type.accentBorder : 'border-slate-200'} overflow-hidden transition-all hover:shadow-md`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2.5">
          <div className={`w-8 h-8 rounded-lg ${type.iconBg} flex items-center justify-center flex-shrink-0`}>
            <Icon size={15} className={type.iconColor} />
          </div>
          <div className="min-w-0">
            <h3 className="text-[13px] font-semibold text-slate-800 truncate">{type.title}</h3>
            <div className="flex items-center gap-2 mt-0.5">
              {isEnabled ? (
                <span className="text-[10px] text-emerald-600 font-medium flex items-center gap-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Enabled
                </span>
              ) : (
                <span className="text-[10px] text-slate-400 flex items-center gap-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-300" /> Not configured
                </span>
              )}
              {config.scheduleFrequency && (
                <span className="text-[10px] text-slate-400">
                  · {FREQ_LABELS[config.scheduleFrequency] || config.scheduleFrequency}
                  {config.scheduleTime && ` at ${formatTime(config.scheduleTime)}`}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {type.endpoint && (
            <button
              onClick={onRunNow}
              disabled={running || isBackendRunning}
              title={isBackendRunning ? 'Processing in progress...' : 'Run Now'}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-lg transition-colors disabled:opacity-50 border ${
                isBackendRunning
                  ? 'text-amber-600 bg-amber-50 border-amber-200'
                  : 'text-[#C45500] bg-[#FFF4E6] hover:bg-[#FFE8C8] border-[#FFE8C8]'
              }`}
            >
              {(running || isBackendRunning) ? <Loader2 size={11} className="animate-spin" /> : <Play size={11} />}
              {isBackendRunning ? 'Processing...' : running ? 'Starting...' : 'Run Now'}
            </button>
          )}
          <button onClick={onNavigate} title="Open page" className="p-1.5 rounded-lg text-slate-400 hover:text-[#C45500] hover:bg-[#FFF4E6] transition-colors">
            <ExternalLink size={13} />
          </button>
        </div>
      </div>

      {/* Running Banner */}
      {isBackendRunning && (
        <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-100 flex items-center gap-2">
          <Loader2 size={12} className="animate-spin text-amber-600" />
          <span className="text-[11px] text-amber-700 font-medium">Processing in progress... Results will appear here when complete.</span>
        </div>
      )}

      {/* Content */}
      {hasDelivery ? (
        <div className="px-4 py-3 space-y-2">
          {/* Subject */}
          <p className="text-[12px] font-medium text-slate-700 truncate">{delivery.subject}</p>

          {/* Message preview */}
          <div className="relative group/msg">
            <div className="bg-slate-50 rounded-lg px-3 py-2 cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => setShowContent(true)}>
              <p className="text-[11px] text-slate-500 line-clamp-3 whitespace-pre-line leading-relaxed">
                {delivery.messageContent?.substring(0, 300)}{delivery.messageContent?.length > 300 ? '...' : ''}
              </p>
            </div>
            <button
              onClick={() => setShowContent(true)}
              className="absolute top-1.5 right-1.5 p-1 rounded-md bg-white/80 backdrop-blur border border-slate-200 text-slate-400 hover:text-[#C45500] hover:border-[#FFD89E] transition-all opacity-0 group-hover/msg:opacity-100 shadow-sm"
              title="View full content"
            >
              <Maximize2 size={11} />
            </button>
          </div>

          {/* Content Viewer Modal */}
          <ContentViewerModal
            isOpen={showContent}
            onClose={() => setShowContent(false)}
            title={delivery.subject}
            content={delivery.messageContent}
          />

          {/* Footer */}
          <div className="flex items-center justify-between pt-1">
            <div className="flex items-center gap-3">
              <span className={`inline-flex items-center gap-1 text-[10px] font-medium ${ss.text}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${ss.dot}`} />
                {status || 'Unknown'}
              </span>
              {delivery.channelType && (
                <span className="inline-flex items-center gap-1 text-[10px] text-slate-400">
                  <Mail size={9} /> {delivery.channelType}
                </span>
              )}
              {delivery.totalRecipients > 0 && (
                <span className="text-[10px] text-slate-400">
                  {delivery.sentCount || 0}/{delivery.totalRecipients} sent
                </span>
              )}
            </div>
            <span className="text-[10px] text-slate-400 flex items-center gap-1">
              <Clock size={9} /> {timeAgo(delivery.triggeredAt)}
            </span>
          </div>

          {/* Schedule info */}
          {(config.lastRunAt || config.nextRunAt) && (
            <div className="flex items-center gap-4 pt-1.5 border-t border-slate-100 text-[10px] text-slate-400">
              {config.lastRunAt && <span>Last: {formatDateTime(config.lastRunAt)}</span>}
              {config.nextRunAt && <span className="text-[#f08804] font-medium">Next: {formatDateTime(config.nextRunAt)}</span>}
            </div>
          )}
        </div>
      ) : (
        <div className="px-4 py-5 text-center">
          <p className="text-[11px] text-slate-400">
            {isBackendRunning ? 'Processing... Results will appear here shortly.' : type.endpoint ? 'No data yet — click Run Now to generate' : 'Coming soon'}
          </p>
          {(config.lastRunAt || config.nextRunAt) && (
            <div className="flex items-center justify-center gap-4 mt-2 text-[10px] text-slate-400">
              {config.lastRunAt && <span>Last: {formatDateTime(config.lastRunAt)}</span>}
              {config.nextRunAt && <span className="text-[#f08804] font-medium">Next: {formatDateTime(config.nextRunAt)}</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Dashboard ──────────────────────────────────────────────────────────

export default function SellerDashboardPage() {
  const navigate = useNavigate();
  const { selectedSeller } = useSeller();
  const [loading, setLoading] = useState(true);
  const [summaryData, setSummaryData] = useState([]);
  const [runningTypes, setRunningTypes] = useState({});

  const sellerAccountId = useMemo(() => {
    if (!selectedSeller?.channels) return null;
    const ch = selectedSeller.channels.find(c => c.accountStatus === 'AUTHORIZED');
    return ch?.id || selectedSeller.channels[0]?.id || null;
  }, [selectedSeller]);

  useEffect(() => {
    if (!selectedSeller) {
      navigate('/sellers');
      return;
    }
    loadSummary();
  }, [selectedSeller]);

  const loadSummary = async () => {
    if (!selectedSeller?.id) return;
    setLoading(true);
    try {
      const res = await apiClient.get(ENDPOINTS.DASHBOARD.SUMMARY, {
        params: { sellerId: selectedSeller.id }
      });
      // Interceptor returns camelcaseKeys(response.data) directly, so res IS the data
      const raw = res.data || res;
      const data = Array.isArray(raw) ? raw : [];
      setSummaryData(data);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  // Auto-refresh every 15 seconds when any notification is running
  const anyRunning = useMemo(() =>
    summaryData.some(item => item.isRunning === true), [summaryData]);

  useEffect(() => {
    if (!anyRunning) return;
    const interval = setInterval(() => loadSummary(), 15000);
    return () => clearInterval(interval);
  }, [anyRunning]);

  // Build a map of notificationType → summary data
  const summaryMap = useMemo(() => {
    const map = {};
    for (const item of summaryData) {
      if (item.notificationType) {
        map[item.notificationType] = item;
      }
    }
    return map;
  }, [summaryData]);

  const handleRunNow = async (type) => {
    if (!sellerAccountId) {
      toast.error('No marketplace connected');
      return;
    }
    setRunningTypes(prev => ({ ...prev, [type.id]: true }));
    try {
      const res = await apiClient.post(type.endpoint, null, { params: { sellerAccountId } });
      const raw = res.data || res;

      if (raw.status === 'ALREADY_RUNNING') {
        toast('Already running — please wait for it to complete', { icon: '⏳' });
      } else if (raw.status === 'ACCEPTED') {
        toast.success('Processing started — check back in a moment');
      } else {
        toast.success(`${type.title} completed`);
      }

      // Refresh dashboard to pick up RUNNING/COMPLETED status
      setTimeout(() => loadSummary(), 2000);
    } catch (err) {
      toast.error(`Failed: ${err?.response?.data?.message || err.message}`);
    } finally {
      setRunningTypes(prev => ({ ...prev, [type.id]: false }));
    }
  };

  if (!selectedSeller) return null;

  return (
    <AdminLayout>
      <div className="p-3 sm:p-5 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl font-bold text-slate-900">Dashboard</h1>
            <p className="text-xs sm:text-sm text-slate-500 mt-0.5 truncate">
              {selectedSeller.businessName}
            </p>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            <button
              onClick={loadSummary}
              disabled={loading}
              className="p-2 rounded-lg bg-white border border-[#D5D9D9] text-[#565959] hover:bg-[#F7F7F7] transition-colors disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </button>
            <button
              onClick={() => navigate('/settings/notification-config')}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-[12px] font-medium text-[#565959] bg-white border border-[#D5D9D9] rounded-lg hover:bg-[#F7F7F7] transition-colors"
            >
              <Bell size={13} />
              <span className="hidden sm:inline">Configure</span>
            </button>
          </div>
        </div>

        {/* Notification Badges Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={32} className="text-[#f08804] animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {NOTIFICATION_TYPES.map(type => (
              <NotificationBadge
                key={type.id}
                type={type}
                data={summaryMap[type.id]}
                running={runningTypes[type.id] || false}
                onRunNow={() => handleRunNow(type)}
                onNavigate={() => navigate(type.route)}
              />
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
